import type { QueryClient, QueryKey } from '@tanstack/react-query';

import type { NextUpData, PlanViewData, TaskCardData, TaskDetailsData } from '../types/planner';
import { localDayOf } from '../utils/plannerDates';
import { plannerKeys } from './plannerQuery';
import { sameRef, type TaskRef } from './taskRef';

export type CardUpdate = (card: TaskCardData) => TaskCardData | null;

export type PlannerSnapshot = Array<[QueryKey, unknown]>;

const cardDay = (card: TaskCardData): string | null => (card.planned_at ? localDayOf(card.planned_at) : card.day);

function updateList(list: TaskCardData[], ref: TaskRef, update: CardUpdate): TaskCardData[] {
  return list.flatMap((card) => {
    if (!sameRef(card, ref)) return [card];
    const next = update(card);
    return next ? [next] : [];
  });
}

export function updatePlanView(plan: PlanViewData, ref: TaskRef, update: CardUpdate): PlanViewData {
  const current = [...plan.days.flatMap((day) => day.tasks), ...plan.not_planned.first, ...plan.due_in_range].find(
    (card) => sameRef(card, ref),
  );
  if (!current) return plan;
  const moved = update(current);
  const target = moved ? cardDay(moved) : null;
  const days = plan.days.map((day) => {
    const rest = day.tasks.filter((card) => !sameRef(card, ref));
    return { ...day, tasks: moved && day.day === target ? [...rest, moved] : rest };
  });
  return {
    ...plan,
    days,
    due_in_range: updateList(plan.due_in_range, ref, update),
    not_planned: { ...plan.not_planned, first: updateList(plan.not_planned.first, ref, update) },
  };
}

function updateEntry(key: QueryKey, data: unknown, ref: TaskRef, update: CardUpdate): unknown {
  if (data == null) return data;
  switch (key[1]) {
    case 'plan':
      return updatePlanView(data as PlanViewData, ref, update);
    case 'not-planned':
    case 'class-tasks':
      return updateList(data as TaskCardData[], ref, update);
    case 'next-up': {
      const nextUp = data as NextUpData;
      if (!sameRef(nextUp.card, ref)) return nextUp;
      const card = update(nextUp.card);
      return card ? { ...nextUp, card } : null;
    }
    case 'details': {
      const details = data as TaskDetailsData;
      if (!sameRef(details, ref)) return details;
      const card = update(details);
      return card ? { ...details, ...card } : details;
    }
    default:
      return data;
  }
}

export async function snapshotPlanner(queryClient: QueryClient): Promise<PlannerSnapshot> {
  await queryClient.cancelQueries({ queryKey: plannerKeys.all });
  return queryClient.getQueriesData({ queryKey: plannerKeys.all });
}

export function restorePlanner(queryClient: QueryClient, snapshot: PlannerSnapshot | undefined): void {
  snapshot?.forEach(([key, data]) => queryClient.setQueryData(key, data));
}

export function updatePlannerCards(queryClient: QueryClient, ref: TaskRef, update: CardUpdate): void {
  queryClient.getQueriesData({ queryKey: plannerKeys.all }).forEach(([key, data]) => {
    const next = updateEntry(key, data, ref, update);
    if (next !== data) queryClient.setQueryData(key, next);
  });
}
